import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { TCompareVersionsButtonProps } from '@/console/features/projects/components/compare-versions-button/compare-versions-button.types';
import { useConversionStatus } from '@/console/features/projects/hooks/use-conversion-status/use-conversion-status';
import { CompareIcon } from '@/shared/assets/icons/compare-icon';
import { Button } from '@/shared/components/ui/button/button';
import { JobStatusEnum } from '@/shared/types/types.generated';

export const CompareVersionsButton: React.FC<TCompareVersionsButtonProps> = ({
  projectId,
  baseVersion,
  targetVersion,
  onCallback,
  tooltip,
  tooltipProps,
}) => {
  const { t } = useTranslation();
  const { status: baseStatus, isLoading: isBaseLoading } = useConversionStatus({
    versionId: baseVersion,
    projectId,
  });
  const { status: targetStatus, isLoading: isTargetLoading } =
    useConversionStatus({ versionId: targetVersion, projectId });

  const navigate = useNavigate();

  const compareVersions = () => {
    onCallback?.();
    navigate(`/projects/${projectId}/compare/${baseVersion}/${targetVersion}`);
  };

  const getStatusTooltip = () => {
    if (
      targetStatus !== JobStatusEnum.Success ||
      baseStatus !== JobStatusEnum.Success
    ) {
      return t('projects:conversion_in_progress');
    }

    if (isBaseLoading || isTargetLoading) {
      return t('projects:loading_conversion_status');
    }

    return t('projects:conversion_not_successful');
  };

  const disabled =
    isBaseLoading ||
    isTargetLoading ||
    targetStatus !== JobStatusEnum.Success ||
    baseStatus !== JobStatusEnum.Success;

  return (
    <Button
      tooltip={disabled ? getStatusTooltip() : tooltip}
      onClick={compareVersions}
      disabled={disabled}
      icon={<CompareIcon />}
      size="extraSmall"
      variant="secondary"
      tooltipProps={tooltipProps}
    >
      {t('common:compare')}
    </Button>
  );
};
